import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { apiGet, apiPatch, apiPost, handleApiError, Btn, formatDateTime } from "./shared";

const URGENZA_OPTIONS = [
  { value: "normale", label: "Normale" },
  { value: "urgente", label: "Urgente" },
  { value: "critica", label: "Critica" },
];

const STATO_LABELS = {
  bozza: "Bozza",
  inviato: "Inviato",
  ricevuto: "Ricevuto",
  confermato: "Confermato",
  parziale: "Parziale",
  rifiutato: "Rifiutato",
  in_preparazione: "In preparazione",
  pronto: "Pronto",
  consegnato: "Consegnato",
  annullato: "Annullato",
  spedito: "Pronto",
  completato: "Consegnato",
};

function ospiteLabel(o) {
  const nome = [o.cognome, o.nome].filter(Boolean).join(" ").trim();
  const cf = (o.codice_fiscale || o.cf || "").toUpperCase();
  return nome ? `${nome}${cf ? ` (${cf})` : ""}` : cf || o.id;
}

/** Path relativi: struttura (ricette) vs OSS (/ordini-farmacia). */
function resolvePaths(apiBase) {
  const isOss = typeof apiBase === "string" && apiBase.includes("ordini-farmacia");
  if (isOss) {
    return {
      catalogo: "/catalogo",
      ordini: "",
      ospiti: "/ospiti",
      annulla: (id) => `/${encodeURIComponent(id)}/annulla`,
      sostituzione: (id) => `/${encodeURIComponent(id)}/sostituzione`,
    };
  }
  return {
    catalogo: "/ordini-prodotti/catalogo",
    ordini: "/ordini-prodotti",
    ospiti: "/ospiti",
    annulla: (id) => `/ordini-prodotti/${encodeURIComponent(id)}/annulla`,
    sostituzione: (id) => `/ordini-prodotti/${encodeURIComponent(id)}/sostituzione`,
  };
}

/**
 * @param {object} props
 * @param {string} props.token
 * @param {string} [props.apiBase] path o URL base (default: /api/deblistering/ricette)
 * @param {string} [props.reloadKey] forza reload (es. id struttura attiva)
 */
export default function OrdiniProdotti({ token, apiBase, reloadKey }) {
  const paths = useMemo(() => resolvePaths(apiBase), [apiBase]);
  const [catalogo, setCatalogo] = useState([]);
  const [ordini, setOrdini] = useState([]);
  const [ospiti, setOspiti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cf, setCf] = useState("");
  const [ospiteNome, setOspiteNome] = useState("");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [urgenza, setUrgenza] = useState("normale");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [cat, ord, osp] = await Promise.all([
        apiGet(paths.catalogo, token, apiBase),
        apiGet(paths.ordini, token, apiBase),
        apiGet(paths.ospiti, token, apiBase),
      ]);
      setCatalogo(cat.items || []);
      setOrdini(ord.items || []);
      setOspiti(osp.items || []);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [token, apiBase, paths, reloadKey]);

  useEffect(() => { load(); }, [load]);

  const onSelectOspite = (value) => {
    const found = ospiti.find((o) => (o.codice_fiscale || o.cf || "") === value);
    setCf(value);
    if (found) {
      setOspiteNome([found.cognome, found.nome].filter(Boolean).join(" ").trim());
    }
  };

  const searchCatalogo = async () => {
    try {
      const q = search.trim() ? `?q=${encodeURIComponent(search)}` : "";
      const res = await apiGet(`${paths.catalogo}${q}`, token, apiBase);
      setCatalogo(res.items || []);
    } catch (err) {
      handleApiError(err);
    }
  };

  const addLibero = () => {
    if (!cf.trim()) {
      toast.error("Seleziona un ospite o inserisci il codice fiscale");
      return;
    }
    const nome = window.prompt("Descrizione prodotto non in catalogo");
    if (!nome || !nome.trim()) return;
    setCart((c) => [...c, {
      codice_fiscale: cf.trim().toUpperCase(),
      ospite_nome: ospiteNome || undefined,
      prodotto_codice: "",
      prodotto_nome: nome.trim(),
      quantita: 1,
      quantita_richiesta: 1,
      da_identificare: true,
      prezzo: null,
    }]);
    toast.success("Prodotto da identificare aggiunto");
  };

  const addToCart = (prodotto) => {
    if (!cf.trim()) {
      toast.error("Seleziona un ospite o inserisci il codice fiscale");
      return;
    }
    setCart((c) => [...c, {
      codice_fiscale: cf.trim().toUpperCase(),
      ospite_nome: ospiteNome || undefined,
      prodotto_codice: prodotto.codice || prodotto.id,
      prodotto_nome: prodotto.nome,
      quantita: 1,
      quantita_richiesta: 1,
      prezzo: prodotto.prezzo ?? null,
    }]);
    toast.success("Prodotto aggiunto");
  };

  const updateCartQty = (idx, qty) => {
    const n = Math.max(1, parseInt(qty, 10) || 1);
    setCart((c) => c.map((r, i) => (i === idx ? { ...r, quantita: n, quantita_richiesta: n } : r)));
  };

  const submitOrder = async () => {
    if (cart.length === 0) {
      toast.error("Aggiungi almeno un prodotto");
      return;
    }
    setSaving(true);
    try {
      await apiPost(paths.ordini, token, {
        righe: cart.map((r) => ({
          codice_fiscale: r.codice_fiscale,
          ospite_nome: r.ospite_nome,
          prodotto_codice: r.prodotto_codice || undefined,
          prodotto_nome: r.prodotto_nome,
          quantita: r.quantita_richiesta || r.quantita || 1,
          quantita_richiesta: r.quantita_richiesta || r.quantita || 1,
          prezzo: r.prezzo,
          da_identificare: !!r.da_identificare,
        })),
        note: note.trim() || undefined,
        urgenza,
      }, apiBase);
      toast.success("Ordine inviato alla farmacia");
      setCart([]);
      setNote("");
      setUrgenza("normale");
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const annullaOrdine = async (id) => {
    try {
      await apiPatch(paths.annulla(id), token, {}, apiBase);
      toast.success("Ordine annullato");
      load();
    } catch (err) {
      handleApiError(err);
    }
  };

  const rispostaSostituzione = async (id, azione) => {
    try {
      await apiPatch(paths.sostituzione(id), token, { azione }, apiBase);
      toast.success(azione === "accetta_sostituzione" ? "Sostituzione accettata" : "Sostituzione rifiutata");
      load();
    } catch (err) {
      handleApiError(err);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" /> Ordina prodotti
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <select
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm flex-1 min-w-[200px] bg-white"
              value={cf}
              onChange={(e) => onSelectOspite(e.target.value)}
            >
              <option value="">Seleziona ospite…</option>
              {ospiti.map((o) => {
                const ocf = (o.codice_fiscale || o.cf || "").toUpperCase();
                if (!ocf) return null;
                return (
                  <option key={o.id || ocf} value={ocf}>{ospiteLabel(o)}</option>
                );
              })}
            </select>
            <input
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm flex-1 min-w-[160px]"
              placeholder="Oppure CF ospite"
              value={cf}
              onChange={(e) => {
                setCf(e.target.value.toUpperCase());
                setOspiteNome("");
              }}
            />
            <input
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm flex-1 min-w-[160px]"
              placeholder="Cerca prodotto…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchCatalogo()}
            />
            <Btn variant="secondary" onClick={searchCatalogo}>Cerca</Btn>
            <Btn variant="ghost" onClick={addLibero}>Prodotto libero</Btn>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <label className="text-sm text-slate-600">Urgenza</label>
            <select
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
              value={urgenza}
              onChange={(e) => setUrgenza(e.target.value)}
            >
              {URGENZA_OPTIONS.map((u) => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
            <input
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm flex-1 min-w-[200px]"
              placeholder="Note ordine (opzionale)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Catalogo prodotti</h4>
              {catalogo.length === 0 ? (
                <p className="text-sm text-slate-500">Catalogo vuoto o farmacia non collegata.</p>
              ) : (
                <ul className="space-y-2 max-h-64 overflow-y-auto">
                  {catalogo.map((p) => (
                    <li key={p.id || p.codice} className="flex justify-between items-center border border-slate-100 rounded-lg p-2 text-sm">
                      <span>
                        {p.nome} <span className="text-slate-400">({p.codice})</span>
                        {p.prezzo != null ? (
                          <span className="text-slate-400 ml-1">€ {Number(p.prezzo).toFixed(2)}</span>
                        ) : null}
                      </span>
                      <Btn variant="ghost" onClick={() => addToCart(p)}><Plus className="w-4 h-4" /></Btn>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Carrello ordine</h4>
              {cart.length === 0 ? (
                <p className="text-sm text-slate-500">Nessun prodotto nel carrello.</p>
              ) : (
                <ul className="space-y-2 max-h-64 overflow-y-auto">
                  {cart.map((r, i) => (
                    <li key={i} className="text-sm border border-slate-100 rounded-lg p-2 space-y-1">
                      <div className="font-medium">{r.prodotto_nome}</div>
                      <div className="text-slate-500">
                        {r.ospite_nome ? `${r.ospite_nome} · ` : ""}CF: {r.codice_fiscale}
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-500">Qtà</label>
                        <input
                          type="number"
                          min={1}
                          className="w-16 rounded border border-slate-200 px-2 py-1 text-sm"
                          value={r.quantita}
                          onChange={(e) => updateCartQty(i, e.target.value)}
                        />
                        <button
                          type="button"
                          className="text-red-500 text-xs ml-auto"
                          onClick={() => setCart(cart.filter((_, j) => j !== i))}
                        >
                          <Trash2 className="w-3 h-3 inline" /> Rimuovi
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {cart.length > 0 && (
                <Btn onClick={submitOrder} disabled={saving} className="mt-3 w-full">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia ordine alla farmacia"}
                </Btn>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Ordini recenti</CardTitle></CardHeader>
        <CardContent>
          {ordini.length === 0 ? (
            <p className="text-sm text-slate-500">Nessun ordine.</p>
          ) : (
            <ul className="space-y-3">
              {ordini.map((o) => {
                const canAnnulla = ["bozza", "inviato", "ricevuto"].includes(o.stato);
                return (
                  <li key={o.id} className="text-sm border border-slate-100 rounded-lg p-3 space-y-2">
                    <div className="flex flex-wrap justify-between gap-2 items-center">
                      <span className="text-slate-600">{formatDateTime(o.created_at)}</span>
                      <div className="flex items-center gap-2">
                        {o.urgenza && o.urgenza !== "normale" ? (
                          <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-700">
                            {o.urgenza}
                          </Badge>
                        ) : null}
                        <Badge variant="secondary">{STATO_LABELS[o.stato] || o.stato}</Badge>
                        {canAnnulla ? (
                          <button
                            type="button"
                            className="text-xs text-red-600 hover:underline"
                            onClick={() => annullaOrdine(o.id)}
                          >
                            Annulla
                          </button>
                        ) : null}
                        {(o.righe || []).some((r) => r.sostituzione_proposta?.prodotto_nome) ? (
                          <>
                            <button
                              type="button"
                              className="text-xs text-emerald-700 hover:underline"
                              onClick={() => rispostaSostituzione(o.id, "accetta_sostituzione")}
                            >
                              Accetta sostituzione
                            </button>
                            <button
                              type="button"
                              className="text-xs text-amber-700 hover:underline"
                              onClick={() => rispostaSostituzione(o.id, "rifiuta_sostituzione")}
                            >
                              Rifiuta sostituzione
                            </button>
                          </>
                        ) : null}
                      </div>
                    </div>
                    {o.note ? <p className="text-xs text-slate-500">Note: {o.note}</p> : null}
                    {o.note_farmacia ? (
                      <p className="text-xs text-emerald-700">Farmacia: {o.note_farmacia}</p>
                    ) : null}
                    <ul className="space-y-1">
                      {(o.righe || []).map((r, idx) => (
                        <li key={idx} className="text-xs text-slate-600 flex flex-wrap gap-x-3 gap-y-0.5">
                          <span className="font-medium text-slate-800">{r.prodotto_nome}</span>
                          <span>{r.ospite_nome || r.codice_fiscale}</span>
                          <span>rich. {r.quantita_richiesta ?? r.quantita ?? "—"}</span>
                          <span>conf. {r.quantita_confermata ?? "—"}</span>
                          <span>cons. {r.quantita_consegnata ?? "—"}</span>
                          {r.prezzo != null ? <span>€ {Number(r.prezzo).toFixed(2)}</span> : null}
                          {r.sostituzione_proposta?.prodotto_nome ? (
                            <span className="text-amber-700">
                              sost. proposta: {r.sostituzione_proposta.prodotto_nome}
                            </span>
                          ) : null}
                          {r.da_identificare || r.stato === "da_identificare" ? (
                            <span className="text-violet-700">Da identificare</span>
                          ) : null}
                          {r.stato ? <span className="text-slate-400">[{r.stato}]</span> : null}
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
